import { FormContainer } from "@/components/form/FormContainer";
import { useResourceCreate } from "@/hooks/useResourceCreate";
import { useResourceNavigate } from "@/hooks/useResourceNavigate";
import { CreationWrapper } from "@/layouts/CreationWrapper";
import { useTranslation } from "react-i18next";
import { useResourceContext } from "@/layouts/ResourceContext";
import { RESOURCE_TYPE_INSTANCES, RESOURCE_TYPE_USERS } from "@/resources-def";
import { Button, Space } from "antd";
import { useRef, useEffect, useState } from "react";
import { useList, useHandleNotification } from "@refinedev/core";

const NEW_DEFAULT_VALUE = {
  storeInCognito: true,
};

const RESOURCE = RESOURCE_TYPE_USERS;

export const UserCreation = () => {
  const formRef = useRef();
  const handleNotification = useHandleNotification();
  const { t } = useTranslation();
  const navigate = useResourceNavigate();
  const { mutateAsync } = useResourceCreate({
    resource: RESOURCE,
    mutationOptions: {
      onSuccess: (data, variables) => {
        handleNotification({
          message: t(`uses.actionResult.create.success`, { ...variables?.values, ...data }),
          type: "success"
        });
        navigate({ resource: RESOURCE_TYPE_USERS, action: "list", queryParam: "?toLastPage=1" });
      },
    },
  });
  const { tenantName } = useResourceContext();

  const {data: instanceList, isLoading} = useList({
    resource: RESOURCE_TYPE_INSTANCES,
  })

  const [instances, setInstances] = useState(); 

  useEffect(() => {
    if(instanceList) {
      const v = instanceList.data.reduce((accumulator, item) => {
        if(item.tenant == tenantName) {
          accumulator[item.id] = { text: item.name };
        }
        return accumulator;
      }, {});
      setInstances(v);
    }
  },[instanceList])

  const rawValue = NEW_DEFAULT_VALUE;

  const rawSchema = [
    {
      i18nTitle: "config.tenants.form.name",
      title: "User Name",
      dataIndex: "name",
      width: "md",
    },
    {
      i18nTitle: "users.fields.email",
      title: "Email",
      dataIndex: "email",
      width: "md",
      formItemProps: {
        rules: [
          {
            required: true,
            message: `Email is required.`,
          },
        ]
      }
    },
    {
      i18nTitle: "users.fields.fullname",
      title: "Full Name",
      dataIndex: "fullname",
      width: "md",
    },
    {
      i18nTitle: "user.fields.instances",
      title: "Instances",
      dataIndex: "instances",
      valueType: "select",
      valueEnum: instances,
      width: "328px",
      fieldProps: {
        mode: 'multiple',
        loading: isLoading,
      },
    },
    {
      i18nTitle: "users.fields.password",
      title: "Password",
      dataIndex: "password",
      valueType: "password",
      width: "md",
      fieldProps: {
        autoComplete: "new-password",
      },
    },
  ];
  if (tenantName != "__root__") {
    rawSchema.push({
      i18nTitle: "users.form.isAdmin",
      title: "Admin",
      dataIndex: "isAdmin",
      width: "md",
      valueType: "switch",
    });
  } else {
    rawSchema.push({
      i18nTitle: "users.form.storage",
      title: "Store in Cognito",
      dataIndex: "storeInCognito",
      width: "md",
      valueType: "switch",
    });
  }

  const doCreateUser = async (values) => {
    try {
      values["instances"] = values["instances"]?.join(",") || "";
      const resp = await mutateAsync({ values });
      console.log(resp);
    } catch (error) {
      formRef.current.setFields([
        {
          name: "name",
          errors: [error.message],
        },
      ]);
    }
  };

  return (
    <CreationWrapper
      i18nTitle="users.creation.title"
      desc={[]}
      footer={
        <Space>
          <Button
            onClick={() => {
              formRef.current.submit();
              navigate({ resource: RESOURCE, action: "list" });
            }}
          >
            Cancel
          </Button>
          <Button type="primary" onClick={() => formRef.current.submit()}>
            Create
          </Button>
        </Space>
      }
    >
      <FormContainer
        formRef={formRef}
        initValue={rawValue}
        schema={rawSchema}
        onFinishCallback={doCreateUser}
        style={{ paddingTop: 24 }}
      />
    </CreationWrapper>
  );
};
